import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Container,
  Paper
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { passwordResetService } from '../../services/passwordResetService';
import { BRAND_GRADIENT } from '../../constants/branding';
import EapLogo from '../../components/brand/EapLogo';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [errors, setErrors] = useState<{ email?: string }>({});

  // Validar email
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    
    if (!email.trim()) {
      setErrors({ email: 'El email es requerido' });
      return false;
    } else if (!isValid) {
      setErrors({ email: 'El email no es válido' });
      return false;
    } else {
      setErrors({});
      return true;
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail(email)) {
      return;
    }

    setLoading(true);
    setAlert(null);

    try {
      // ✅ USAR SERVICIO en lugar de fetch directo
      const data = await passwordResetService.requestPasswordReset(email);

      if (data.success) {
        setEmailSent(true);
        setAlert({
          type: 'success',
          message: data.message
        });
      } else {
        setAlert({
          type: 'error',
          message: data.message || 'Error al procesar la solicitud'
        });
      }
    } catch (error: any) {
      setAlert({
        type: 'error',
        message: error.message || 'Error de conexión. Inténtalo nuevamente.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: BRAND_GRADIENT, display: 'flex', alignItems: 'center' }}>
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper 
          elevation={6}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 3,
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ mb: 2 }}>
              <EapLogo variant="stacked" />
            </Box>
            <EmailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography component="h1" variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {emailSent ? '¡Email Enviado!' : '¿Olvidaste tu Contraseña?'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {emailSent 
                ? 'Revisa tu bandeja de entrada' 
                : 'No te preocupes, te ayudamos a recuperarla'
              }
            </Typography>
          </Box>

          {/* Alert */}
          {alert && (
            <Alert 
              severity={alert.type} 
              sx={{ width: '100%', mb: 3 }}
              onClose={() => setAlert(null)}
            >
              {alert.message}
            </Alert>
          )}

          {!emailSent ? (
            /* Formulario de solicitud */
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) {
                    setErrors({});
                  }
                }}
                error={!!errors.email}
                helperText={errors.email || 'Te enviaremos un enlace para restablecer tu contraseña'}
                margin="normal"
                autoComplete="email"
                autoFocus
                InputProps={{
                  startAdornment: <EmailIcon sx={{ color: 'action.active', mr: 1 }} />,
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{ 
                  py: 1.5, 
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  mb: 3
                }}
              >
                {loading ? 'Enviando...' : 'Enviar Enlace de Recuperación'}
              </Button>
            </Box>
          ) : (
            /* Mensaje de éxito */
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Si el email <strong>{email}</strong> está registrado en nuestro sistema, 
                recibirás un enlace para restablecer tu contraseña.
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                • Revisa tu bandeja de entrada y spam<br />
                • El enlace expira en 1 hora<br />
                • Si no lo recibes, intenta nuevamente
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                  setAlert(null);
                }}
                sx={{ mb: 2, textTransform: 'none' }}
              >
                Enviar a Otro Email
              </Button>
            </Box>
          )}

          {/* Link de regreso */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Link
              component={RouterLink}
              to="/login"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                color: 'primary.main',
                fontSize: '0.95rem',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              <ArrowBackIcon sx={{ mr: 1, fontSize: 20 }} />
              Volver al Login
            </Link>
          </Box>

          {/* Footer */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              ¿Problemas para acceder? Contacta al administrador del sistema
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
    </Box>
  );
};

export default ForgotPassword;